/**
 * SEO component that queries for data with
 * Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/how-to/querying-data/use-static-query/
 */

import * as React from "react"
import { useStaticQuery, graphql } from "gatsby"
import { getAbsoluteUrl, getAbsoluteImageUrl } from "../utils/seo"

function Seo({
  title,
  description,
  image,
  pathname,
  type = "website",
  article,
  breadcrumb,
  noindex = false,
  children
}) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
          }
        }
      }
    `
  )

  const siteMetadata = site.siteMetadata
  const metaTitle = title ? `${title} | ${siteMetadata.title}` : siteMetadata.title
  const metaDescription = description || siteMetadata.description
  const metaImage = getAbsoluteImageUrl(image, siteMetadata.siteUrl)
  const metaUrl = getAbsoluteUrl(pathname, siteMetadata.siteUrl)

  // 組織の構造化データ（全ページ共通）
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "BuddhistTemple",
    "@id": `${siteMetadata.siteUrl}/#organization`,
    name: "大聖山 不動寺",
    alternateName: "Taiseizan Fudouji",
    url: siteMetadata.siteUrl,
    logo: {
      "@type": "ImageObject",
      url: `${siteMetadata.siteUrl}/images/hudouji_logo_tate 1.png`,
    },
    image: `${siteMetadata.siteUrl}/images/top_header_1.jpg`,
    description: siteMetadata.description,
    address: {
      "@type": "PostalAddress",
      streetAddress: "宮山町4丁目7-2",
      addressLocality: "豊中市",
      addressRegion: "大阪府",
      postalCode: "560-0056",
      addressCountry: "JP",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 34.81044062474512,
      longitude: 135.4618048313696,
    },
  }

  // WebSite構造化データ（トップページのみ）
  const websiteSchema = pathname === "/" ? {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${siteMetadata.siteUrl}/#website`,
    url: siteMetadata.siteUrl,
    name: siteMetadata.title,
    description: siteMetadata.description,
    publisher: {
      "@id": `${siteMetadata.siteUrl}/#organization`,
    },
  } : null

  // BlogPosting構造化データ（記事ページのみ）
  const blogPostingSchema = article ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${metaUrl}/#article`,
    headline: article.title,
    description: article.description || metaDescription,
    image: article.image ? getAbsoluteImageUrl(article.image, siteMetadata.siteUrl) : metaImage,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt || article.publishedAt,
    author: {
      "@type": "Organization",
      name: "大聖山 不動寺",
    },
    publisher: {
      "@id": `${siteMetadata.siteUrl}/#organization`,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": metaUrl,
    },
  } : null

  // BreadcrumbList構造化データ（パンくずがあるページ）
  const breadcrumbSchema = breadcrumb && breadcrumb.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "ホーム",
        item: siteMetadata.siteUrl,
      },
      ...breadcrumb.map((item, index) => ({
        "@type": "ListItem",
        position: index + 2,
        name: item.name,
        item: getAbsoluteUrl(item.url, siteMetadata.siteUrl),
      })),
    ],
  } : null

  // すべての構造化データを配列にまとめる
  const schemas = [
    organizationSchema,
    websiteSchema,
    blogPostingSchema,
    breadcrumbSchema,
  ].filter(Boolean) // null/undefinedを除外

  return (
    <>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />

      {/* Open Graph */}
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:url" content={metaUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteMetadata.title} />
      <meta property="og:locale" content="ja_JP" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />

      {/* Article specific meta tags */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedAt} />
          {article.updatedAt && (
            <meta property="article:modified_time" content={article.updatedAt} />
          )}
        </>
      )}

      {/* Canonical URL */}
      <link rel="canonical" href={metaUrl} />

      {/* Noindex */}
      {noindex && <meta name="robots" content="noindex" />}

      {/* 構造化データ（JSON-LD） */}
      {schemas.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(schemas.length === 1 ? schemas[0] : schemas)}
        </script>
      )}

      {children}
    </>
  )
}

export default Seo
